import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

const STAGES = ["STUCK","EXPLORING","CHOSEN_A_PATH","STARTED","FIRST_100","FIRST_1000","PCT_25","PCT_50","PCT_75","PCT_100","EXIT_READY"];
const LABELS = {
  STUCK:"STUCK", EXPLORING:"EXPLORING", CHOSEN_A_PATH:"CHOSEN A PATH", STARTED:"STARTED",
  FIRST_100:"FIRST $100", FIRST_1000:"FIRST $1,000",
  PCT_25:"25% REPLACED", PCT_50:"50% REPLACED", PCT_75:"75% REPLACED",
  PCT_100:"100% REPLACED", EXIT_READY:"EXIT READY"
};

export default function Roadmap() {
  const [stage, setStage] = useState("STUCK");
  const [exitData, setExitData] = useState({});
  const [checkins, setCheckins] = useState([]);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0, weeks_logged: 0 });

  useEffect(() => {
    api.get("/auth/me").then(r => setStage(r.data.user.roadmap_stage || "STUCK"));
    api.get("/exit").then(r => setExitData(r.data.exit || {}));
    api.get("/checkins").then(r => setCheckins(r.data.checkins || []));
    api.get("/streak").then(r => setStreak(r.data));
  }, []);

  // Compute effective stage from exit data if higher
  const pct = exitData.pct_job_income_replaced || 0;
  let effective = STAGES.indexOf(stage);
  if (pct >= 100) effective = Math.max(effective, STAGES.indexOf("PCT_100"));
  else if (pct >= 75) effective = Math.max(effective, STAGES.indexOf("PCT_75"));
  else if (pct >= 50) effective = Math.max(effective, STAGES.indexOf("PCT_50"));
  else if (pct >= 25) effective = Math.max(effective, STAGES.indexOf("PCT_25"));

  return (
    <div className="max-w-3xl mx-auto px-5 pt-6">
      <div className="eyebrow text-[#FD0F10]">YOUR ESCAPE ROADMAP</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">One step at a time.</h1>

      <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-3" data-testid="streak-stats">
        <StreakStat label="CURRENT STREAK" value={streak.current_streak} suffix={streak.current_streak === 1 ? "week" : "weeks"} highlight={streak.current_streak >= 3}/>
        <StreakStat label="LONGEST STREAK" value={streak.longest_streak} suffix={streak.longest_streak === 1 ? "week" : "weeks"}/>
        <StreakStat label="WEEKS LOGGED" value={streak.weeks_logged} suffix="total"/>
      </div>
      {streak.current_streak >= 1 && (
        <p className="mt-3 text-sm text-gray-300" data-testid="streak-message">
          {streak.current_streak === 1
            ? "One week in the books. Keep it going. Next Sunday, log again."
            : streak.current_streak < 4
            ? `${streak.current_streak} weeks in a row. Momentum is a real thing. Don't break the chain.`
            : `${streak.current_streak} weeks straight. This is the version of you that builds things. Keep going.`}
        </p>
      )}

      <div className="mt-8">
        {STAGES.map((s, i) => {
          const done = i <= effective;
          return (
            <div key={s} className="flex items-start gap-4 relative" data-testid={`stage-${s}`}>
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-mono-ui text-xs font-bold ${done ? "hero-accent text-white" : "bg-[#14161B] border border-[#2A2E39] text-gray-400"}`}>
                  {done ? "✓" : i + 1}
                </div>
                {i < STAGES.length - 1 && <div className={`w-0.5 flex-1 min-h-[36px] ${done ? "bg-[#FD0F10]" : "bg-[#2A2E39]"}`} />}
              </div>
              <div className="pb-6 flex-1">
                <div className={`font-display font-bold text-lg ${done ? "text-white" : "text-gray-400"}`}>{LABELS[s]}</div>
                {i === effective && <div className="text-xs font-mono-ui uppercase tracking-wider text-[#FD0F10] mt-1">YOU ARE HERE</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 card-panel p-5">
        <div className="eyebrow text-[#FD0F10]">WEEKLY CHECK-INS</div>
        <div className="mt-2 text-sm text-gray-300">Total side income logged: <span className="font-display font-bold text-white">${checkins.reduce((s,c)=>s+(c.earned||0),0).toFixed(2)}</span></div>
        <Link to="/checkin" className="mt-3 inline-block hero-accent px-4 py-2 rounded text-sm font-bold" data-testid="add-checkin">Add this week's check-in</Link>
      </div>
    </div>
  );
}

function StreakStat({ label, value, suffix, highlight }) {
  return (
    <div className={`card-panel p-3 sm:p-4 ${highlight ? "border-[#FD0F10]" : ""}`}>
      <div className="eyebrow text-[#FD0F10] !text-[9px] sm:!text-[0.72rem] leading-tight">{label}</div>
      <div className="mt-1 flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
        <span className="font-display text-2xl sm:text-3xl font-black text-white">{value}</span>
        <span className="text-xs font-mono-ui uppercase tracking-wider text-gray-400">{suffix}</span>
      </div>
    </div>
  );
}
